import {adminAxios} from '@/Plugins/Axios';
import i18n from '@/i18n';

// ຖອນເງິນ COD ລ່ວງໜ້າ — COD Advance Withdraw (admin).
// Everything lives under /api/admin/cod-advance — adminAxios' own baseURL.
// Enrollment / audit need role SuperAdmin|accountingcod AND the
// `manage-cod-advance` permission; the transfer queue additionally allows
// callcentervipcustomer.

const unwrap = (response) => (response.data && response.data.data !== undefined ? response.data.data : response.data);

/* ---------------------------------------------------------------- shops */

export async function fetchShops(params = {}) {
    return unwrap(await adminAxios.get('cod-advance/shops', {params}));
}

export async function fetchShop(id) {
    return unwrap(await adminAxios.get('cod-advance/shops/' + id));
}

export async function createShop(formData) {
    return unwrap(await adminAxios.post('cod-advance/shops', formData, {
        headers: {'Content-Type': 'multipart/form-data'},
    }));
}

// POST (not PUT) so the multipart body survives — the backend routes both.
export async function updateShop(id, formData) {
    return unwrap(await adminAxios.post('cod-advance/shops/' + id, formData, {
        headers: {'Content-Type': 'multipart/form-data'},
    }));
}

export async function searchCodCustomers(q) {
    return unwrap(await adminAxios.get('cod-advance/customers/search', {params: {q}})) || [];
}

export async function fetchCustomerPreview(customerId, ratePct) {
    return unwrap(await adminAxios.get('cod-advance/customers/' + customerId + '/preview', {params: {rate_pct: ratePct}}));
}

/* ------------------------------------------------------------ approvals */

// Enabling a shop, its % withdraw and its % fee wait for ຜູ້ບໍລິຫານ (manager or
// SuperAdmin) unless one of them makes the change (owner, 2026-10-05). The list
// answers `can_approve` for the signed-in user; the server is the boundary.
export async function fetchApprovals(params = {}) {
    return unwrap(await adminAxios.get('cod-advance/approvals', {params}));
}

export async function approveShopChange(id) {
    return unwrap(await adminAxios.post('cod-advance/approvals/' + id + '/approve'));
}

export async function rejectShopChange(id, reason) {
    return unwrap(await adminAxios.post('cod-advance/approvals/' + id + '/reject', {reason}));
}

/* --------------------------------------------------------------- policy */

// ຕັ້ງຄ່າເງື່ອນໄຂ COD ລ່ວງໜ້າ — the boss's ceilings for every shop (owner,
// 2026-10-26): {current, history}. Everyone on the ຮ້ານຄ້າ page reads it; only
// ຜູ້ບໍລິຫານ / SuperAdmin save (the server refuses Finance).
export async function fetchPolicy() {
    return unwrap(await adminAxios.get('cod-advance/policy'));
}

export async function savePolicy(payload) {
    return unwrap(await adminAxios.put('cod-advance/policy', payload));
}

// The fields a change request may carry, in the form's order. The labels are
// locale keys, resolved when the rows are built so they follow the UI language.
const CHANGE_LABEL_KEYS = {
    is_enabled: 'codAdvance.shop_status',
    advance_rate_pct: 'codAdvance.advance_rate_pct',
    fee_pct: 'codAdvance.fee',
};

// A waiting change ({field: {old, new}}) as display rows, in the form's order.
export function describeChanges(changes) {
    const shown = (field, value) => {
        if (field === 'is_enabled') return value ? i18n.t('codAdvance.on') : i18n.t('codAdvanceReport.close');
        return formatPct(value);
    };
    return Object.keys(CHANGE_LABEL_KEYS)
        .filter((field) => changes && changes[field])
        .map((field) => ({
            field,
            label: i18n.t(CHANGE_LABEL_KEYS[field]),
            old: shown(field, changes[field].old),
            new: shown(field, changes[field].new),
        }));
}

// The withdraw modes the API stores (`withdraw_mode`): the VALUES never change;
// label and hint are locale keys, resolved when the options are read.
const WITHDRAW_MODE_KEYS = [
    {value: 'full', label: 'codAdvance.withdraw_mode_full', hint: 'codAdvance.withdraw_mode_full_hint'},
    {value: 'split', label: 'codAdvance.withdraw_mode_split', hint: 'codAdvance.withdraw_mode_split_hint'},
];

// [{value, label, hint}] in the UI language — built at call time, never cached.
export function withdrawModes() {
    return WITHDRAW_MODE_KEYS.map((m) => ({value: m.value, label: i18n.t(m.label), hint: i18n.t(m.hint)}));
}

export function withdrawModeLabel(mode) {
    const found = WITHDRAW_MODE_KEYS.find((m) => m.value === mode);
    return i18n.t((found || WITHDRAW_MODE_KEYS[0]).label);
}

/* ------------------------------------------------------------ transfers */

export async function fetchTransfers(params = {}) {
    return unwrap(await adminAxios.get('cod-advance/transfers', {params}));
}

export async function fetchTransfer(id) {
    return unwrap(await adminAxios.get('cod-advance/transfers/' + id));
}

// Claim first: the bank details are returned only to the operator who claimed
// the row, so two people can never wire the same payout (FR-026b).
export async function claimTransfer(id) {
    return unwrap(await adminAxios.post('cod-advance/transfers/' + id + '/claim'));
}

export async function releaseTransfer(id, reason) {
    return unwrap(await adminAxios.post('cod-advance/transfers/' + id + '/release', {reason}));
}

export async function uploadTransferSlip(id, formData) {
    return unwrap(await adminAxios.post('cod-advance/transfers/' + id + '/slip', formData, {
        headers: {'Content-Type': 'multipart/form-data'},
    }));
}

export async function rejectTransfer(id, reason) {
    return unwrap(await adminAxios.post('cod-advance/transfers/' + id + '/reject', {reason}));
}

// ອັບເດດ N Invoice — the ticked rows were paid in ONE I-Bank transfer, no slip
// (Finance, 2026-10-26), like the legacy COD page: {invoices, paid_at, remark}
// → {updated, skipped, failed}. Rows already paid come back as skipped.
export async function markTransfersPaid(payload) {
    return unwrap(await adminAxios.post('cod-advance/transfers/mark-paid', payload));
}

// The bell: {open_count, latest_id, new_count, items} — items are the newest
// requests above after_id. null = the browser's first poll (counts only); 0 =
// it last saw an empty queue, so every open request is new.
export async function fetchTransferAlerts(afterId = null) {
    const params = afterId === null || afterId === undefined ? {} : {after_id: afterId};
    return unwrap(await adminAxios.get('cod-advance/transfers/alerts', {params}));
}

// ເລືອກທັງໝົດ: the ids of the filtered list, at most one export's worth
// ({ids, total, truncated}).
export async function fetchTransferIds(params = {}) {
    return unwrap(await adminAxios.get('cod-advance/transfers/ids', {params}));
}

// The legacy COD page's exports for this queue: kind = summary | details | bank.
// ⚠️ `bank` (IBank-BCEL / IBank-LDB, payload.bank_name) CLAIMS its rows for the
// caller — whoever takes the bank file is the one wiring the money.
export async function downloadTransferExport(kind, payload) {
    return adminAxios.post('cod-advance/transfers/export/' + kind, payload, {responseType: 'blob'});
}

// A refused export answers JSON, but a blob request reads it as a Blob.
export async function blobErrorMessage(error, fallback) {
    const data = error && error.response ? error.response.data : null;
    if (data && typeof data.text === 'function') {
        try {
            const json = JSON.parse(await data.text());
            return json.message || json.msg || fallback;
        } catch (_) {
            return fallback;
        }
    }
    return errorMessage(error, fallback);
}

/* ---------------------------------------------------------------- audit */

export async function fetchAudit(params = {}) {
    return unwrap(await adminAxios.get('cod-advance/audit', {params}));
}

// Unlike axios `params`, URLSearchParams writes an unset filter as the text
// "undefined" — the export then searched for it and came back empty.
export function auditExportUrl(params = {}) {
    const set = Object.keys(params)
        .filter((k) => params[k] !== undefined && params[k] !== null && params[k] !== '')
        .reduce((out, k) => ({...out, [k]: params[k]}), {});
    const query = new URLSearchParams(set).toString();
    return 'cod-advance/audit/export' + (query ? '?' + query : '');
}

export async function downloadAuditExport(params = {}) {
    return adminAxios.get(auditExportUrl(params), {responseType: 'blob'});
}

// The write-off picker: disbursed advances still owing, oldest first. Always a
// list — the API wraps it as `{advances}` so an empty answer is not dropped.
export async function fetchOpenAdvances(customerId) {
    const data = unwrap(await adminAxios.get('cod-advance/audit/' + customerId + '/advances'));
    return (data && Array.isArray(data.advances)) ? data.advances : [];
}

// ປະຫວັດການຫັກໜີ້: debt movements for a date range (or every date with
// `all_dates`), with per-day totals and the balance each one left.
export async function fetchDebtHistory(params = {}) {
    return unwrap(await adminAxios.get('cod-advance/debt-history', {params}));
}

// ປະຫວັດການຫັກໜີ້ as xlsx, with the tab's filters (axios leaves unset params out).
export async function downloadDebtHistoryExport(params = {}) {
    return adminAxios.get('cod-advance/debt-history/export', {params, responseType: 'blob'});
}

// One invoice of the history: its figures, its bills (paged) and, for an
// advance's invoice, the advance's own figures. No bank details.
export async function fetchDebtHistoryInvoice(id, page = 1) {
    return unwrap(await adminAxios.get('cod-advance/debt-history/invoices/' + id, {params: {page}}));
}

export async function writeOffAdvance(customerId, payload) {
    return unwrap(await adminAxios.post('cod-advance/audit/' + customerId + '/write-off', payload));
}

// ຮັບຊຳລະໜີ້: the shop paid back outside COD — multipart, the slip is required.
export async function repayAdvance(customerId, formData) {
    return unwrap(await adminAxios.post('cod-advance/audit/' + customerId + '/repayment', formData, {
        headers: {'Content-Type': 'multipart/form-data'},
    }));
}

/* --------------------------------------------------------------- shared */

export function formatKip(value) {
    const n = Number(value || 0);
    return n.toLocaleString('en-US') + ' ' + i18n.t('codAdvanceReport.kip_unit');
}

// `30%`, not `30.00%`; null (no advance on the row) reads as a dash.
export function formatPct(value) {
    return value === null || value === undefined || value === '' ? '—' : Number(value) + '%';
}

// Model dates arrive as `d-m-Y H:i:s` (CustomDateTimeCast); raw query columns
// such as the audit's oldest open advance arrive as `Y-m-d H:i:s`.
export function formatDateTime(value) {
    const text = String(value || '').trim();
    let m = text.match(/^(\d{2})-(\d{2})-(\d{4})(?:[ T](\d{2}):(\d{2}))?/);
    if (m) {
        return m[1] + '/' + m[2] + '/' + m[3] + (m[4] ? ' ' + m[4] + ':' + m[5] : '');
    }
    m = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/);
    if (m) {
        return m[3] + '/' + m[2] + '/' + m[1] + (m[4] ? ' ' + m[4] + ':' + m[5] : '');
    }
    return text || '—';
}

export function errorMessage(error, fallback) {
    const data = error && error.response ? error.response.data : null;
    return (data && (data.message || data.msg)) || fallback || i18n.t('codAdvance.generic_error');
}
